import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from "motion/react";
import { useArtistSchedules } from '@/features/artists/hooks';
import { useEventScheduleView } from '@/features/events/hooks';

import {
  CalendarView,
  BookingSection,
  EventPagination
} from '@/features/events/components';
import {
  SchedulesSkeleton,
  ViewModeToggle,
  PastDatesToggle
} from '@/pages/EventSchedulePage/components';
import type { ComputedEventSchedule } from '@/features/events/types/types';

interface ArtistScheduleSectionProps {
  artistId: string;
}

/**
 * Schedule section component for artist page
 * Displays upcoming event schedules in list or calendar view
 */
export const ArtistScheduleSection: React.FC<ArtistScheduleSectionProps> = ({ artistId }) => {
  const navigate = useNavigate();

  // Custom hooks for schedule management
  const {
    schedules,
    schedulesLoading,
    showPastSchedules,
    setShowPastSchedules
  } = useArtistSchedules(artistId);

  // Custom hooks for view state
  const {
    viewMode,
    setViewMode,
    currentMonth,
    setCurrentMonth,
    availableMonths,
    paginatedSchedules
  } = useEventScheduleView(schedules);


  
  // Get timezone from first schedule's timezone_id
  const businessTimeZone = schedules[0]?.timezone_id;

  // Handle booking navigation
  const handleBookTable = (schedule: ComputedEventSchedule) => {
    const eventId = schedule.event?.id;
    const clubHandle = schedule.event?.business?.handle;
    if (eventId && clubHandle) {
      navigate(`/venues/${clubHandle}/events/${eventId}?scheduleId=${schedule.id}&bookingType=tables`);
    }
  };

  const handleBookTickets = (schedule: ComputedEventSchedule) => {
    const eventId = schedule.event?.id;
    const clubHandle = schedule.event?.business?.handle;
    if (eventId && clubHandle) {
      navigate(`/venues/${clubHandle}/events/${eventId}?scheduleId=${schedule.id}&bookingType=tickets`);
    }
  };

  // Don't render if no schedules and not loading
  if (!schedulesLoading && schedules.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-16"
    >
      <h2 className="text-3xl font-serif font-medium text-white mb-8 text-center">
        Upcoming Performances
      </h2>

      {/* View Mode Toggle and Past Dates Toggle */}
      <div className="relative flex justify-center items-center mb-10 min-h-[48px]">
        <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
        <PastDatesToggle
          showPastSchedules={showPastSchedules}
          onToggle={setShowPastSchedules}
        />
      </div>

      <AnimatePresence mode="wait">
        {schedulesLoading ? (
          <SchedulesSkeleton />
        ) : viewMode === 'calendar' ? (
          <motion.div
            key="calendar"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <CalendarView
              schedules={schedules}
              businessTimeZone={businessTimeZone}
              onBookTickets={handleBookTickets}
              onBookTables={handleBookTable}
            />
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {schedules.length > 0 && currentMonth && (
              <div className="mb-10">
                <EventPagination
                  currentMonth={currentMonth}
                  availableMonths={availableMonths}
                  totalItems={paginatedSchedules.length}
                  onMonthChange={setCurrentMonth}
                />
              </div>
            )}
            <BookingSection
              schedules={paginatedSchedules}
              onBookTable={handleBookTable}
              onBookTickets={handleBookTickets}
              businessTimeZone={businessTimeZone}
              showHeading={false}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
